/* eslint-disable max-lines */
import React, { ReactNode, Fragment } from "react";
import { returnFound } from "find-and";
import { uniqueId } from "lodash";
import { Divider } from "@evergis/ui";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon/Icon";

import { HandbookAttributeValue } from "../components/Attributes/components/HandbookAttributeValue";
import { FeatureAttributeField } from "../components/Attributes/FeatureAttributeField";
import { FeatureCardSlideshow } from "../components/FeatureCardSlideshow/FeatureCardSlideshow";
import { FeatureCardChips } from "../components/FeatureCardHeader/FeatureCardChips";
import { FeatureCardChartLegend } from "../components/FeatureCardChartLegend/FeatureCardChartLegend";
import { CameraAttribute } from "../components/CameraAttribute/CameraAttribute";
import { FeatureCardLink } from "../components/FeatureCardLink/FeatureCardLink";
import { FeatureCardTooltip } from "../components/FeatureCardTooltip/FeatureCardTooltip";
import { FeatureCardChart } from "../components/FeatureCardChart/FeatureCardChart";
import { FeatureCardSvg } from "../components/FeatureCardSvg/FeatureCardSvg";
import { ImageContainerButton } from "../containers/styled";
import { ContainerWrapper, FeatureCardChip, FeatureCardIcon } from "../styled";
import { TextTrim } from "../components/TextTrim/TextTrim";
import { getAttribute } from "./getAttribute";
import { getAttributeValue } from "./getAttributeValue";
import { getContainerTemplate } from "./getContainerTemplate";
import { getSvgUrl } from "./getSvgUrl";
import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { isHandbookAttribute } from "./isHandbookAttribute";
import { isVisibleContainer } from "./isVisibleContainer";
import { isEmptyValue } from "./isEmptyValue";
import { FeaturedDcExtendedGeometry } from "../../../ducks/selectFeatures/types";
import { ContainerProps, RenderElementFuncProps, RenderElementProps, WidgetType } from "../types";

export const renderElement =
  ({
    config,
    elementConfig,
    attributes,
    layerInfo,
    selectedTabId,
    setSelectedTabId,
    page,
    expandedContainers,
    type = WidgetType.FeatureCard,
  }: RenderElementFuncProps): ContainerProps["renderElement"] =>
  <IsWrapped extends boolean>(
    props: RenderElementProps & {
      wrap?: IsWrapped;
    },
  ): IsWrapped extends true ? ReactNode : string => {
    const { id, index, wrap } = props;
    const currentConfig = elementConfig || config;
    let element = id ? returnFound(currentConfig?.children, { id }) : index ? currentConfig?.children[index] : null;

    if (Array.isArray(element)) {
      element = element[0];
    }

    const { defaultValue, options, children, style } = element || {};

    const {
      tabId,
      expandable,
      expanded,
      maxLength,
      lineBreak,
      icon,
      trim,
      separator,
      width,
      fontColor,
      fontSize,
      noUnits,
      tagView,
      bgColor,
      withDivider,
      radius,
      hideEmpty,
    } = options || {};

    if (tabId && selectedTabId && (Array.isArray(tabId) ? !tabId.includes(selectedTabId) : tabId !== selectedTabId)) {
      return null;
    }

    const render = renderElement({
      config,
      elementConfig: element,
      attributes,
      layerInfo,
      expandedContainers,
      selectedTabId,
      setSelectedTabId,
      page,
      type,
    });

    const attribute = getAttribute(element, attributes);
    let value: ReactNode | string;

    switch (element?.type) {
      case "text":
        value = element.value;
        break;
      case "attribute":
        value = <FeatureAttributeField attribute={attribute} trim={trim} />;
        break;
      case "attributeValue":
        value = isHandbookAttribute(attribute?.name, layerInfo) ? (
          <HandbookAttributeValue attribute={attribute} element={element} />
        ) : (
          getAttributeValue(element, attributes)
        );
        break;
      case "attributeDescription":
        value = attribute?.name ? layerInfo.layerDefinition.attributes[attribute.name]?.description || null : null;
        break;
      case "attributeAlias": {
        const alias = attribute?.alias || element.attributeName || "";

        value =
          alias && maxLength && maxLength < alias.length ? (
            <TextTrim maxLength={maxLength} expandable={expandable} lineBreak={lineBreak}>
              {alias}
            </TextTrim>
          ) : (
            alias
          );
        break;
      }
      case "attributeUnits":
        value = attribute?.stringFormat?.unitsLabel || null;
        break;
      case "tags":
        value = <FeatureCardChips tags={(attribute?.value as string)?.split(separator || ",")} {...options} />;
        break;
      case "image":
        {
          const firstImage = element.value || (attribute?.value as string)?.split(";")?.[0];

          value = firstImage ? <img src={firstImage} alt={firstImage} width={width} /> : null;
        }
        break;
      case "svg": {
        value = (
          <FeatureCardSvg url={getSvgUrl({ element, layerInfo, attributes })} width={width} fontColor={fontColor} />
        );
        break;
      }
      case "icon": {
        const iconValue = element.attributeName
          ? attributes.find(item => item.name === element.attributeName)?.value
          : element.value;

        value = <FeatureCardIcon kind={iconValue as IconTypesKeys} $fontColor={fontColor} $fontSize={fontSize} />;
        break;
      }
      case "tooltip":
        value = <FeatureCardTooltip text={(attribute?.value as string) || element.value} icon={icon} />;
        break;
      case "link":
        value = <FeatureCardLink link={attribute?.value as string} />;
        break;
      case "button":
        value = (
          <ImageContainerButton onClick={() => window.open(attribute?.value as string)}>
            {element.value || ""}
          </ImageContainerButton>
        );
        break;
      case "slideshow":
        value = (
          <FeatureCardSlideshow elementConfig={element} type={type} attributes={attributes} renderElement={render} />
        );
        break;
      case "chart":
        value = (
          <FeatureCardChart
            config={config}
            elementConfig={currentConfig}
            element={element}
            attributes={attributes}
            type={type}
            renderElement={renderElement({
              config,
              elementConfig,
              attributes,
              expandedContainers,
              type,
              layerInfo,
              selectedTabId,
              setSelectedTabId,
              page,
            })}
          />
        );
        break;
      case "legend": {
        const chartElement = returnFound(config, { id: elementConfig?.options.chartId });

        value =
          !chartElement?.options.expanded || expandedContainers?.[chartElement.id] ? (
            <FeatureCardChartLegend
              config={config}
              elementConfig={currentConfig}
              element={element}
              attributes={attributes}
              type={type}
            />
          ) : null;
        break;
      }
      case "camera":
        value = (
          <CameraAttribute
            value={attribute?.value as string}
            elementConfig={element}
            type={type}
            renderElement={render}
          />
        );
        break;
      default: {
        if (!element?.templateName) return null;

        const ContainerTemplate = getContainerTemplate(element.templateName);

        value = (
          <ContainerTemplate
            config={config}
            elementConfig={element}
            attributes={attributes}
            layerInfo={layerInfo}
            isVisible={isVisibleContainer(id, expandable, expanded, expandedContainers)}
            type={type}
            renderElement={render}
          />
        );
      }
    }

    const valueElement = children?.find(item => item.id === "value");
    const renderedValue = valueElement ? render({ id: "value" }) : null;
    const isEmpty = isEmptyValue(value) || (valueElement && (renderedValue === null || renderedValue === ""));

    if (isEmpty && (hideEmpty || currentConfig?.options?.hideEmpty)) return null;

    const valueOrDefault = isEmpty ? defaultValue : value;
    const resultValue =
      element?.type === "attributeValue" && attribute?.type && attribute?.stringFormat
        ? formatAttributeValue(
            attribute.type,
            valueOrDefault as string | number | Date | FeaturedDcExtendedGeometry,
            attribute.stringFormat,
            noUnits,
          )
        : valueOrDefault;

    return wrap === undefined || wrap ? (
      <Fragment key={uniqueId("renderElement")}>
        {tagView ? (
          <FeatureCardChip
            text={resultValue}
            $bgColor={bgColor}
            $fontColor={fontColor}
            $fontSize={fontSize}
            $radius={radius}
            style={style}
          />
        ) : (
          <ContainerWrapper style={style}>{resultValue}</ContainerWrapper>
        )}
        {withDivider && <Divider />}
      </Fragment>
    ) : (
      resultValue
    );
  };
